import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';

export const cardStyles: SxProps<Theme> = {
    p: 2.5,
    borderRadius: 3,
    border: '1px solid rgba(16,24,40,0.06)',
};

/**
 * A finished or withdrawn purchase, dimmed so the eye lands on what is still running.
 * Text only -- the status chip keeps its colour, because that is the bit being read.
 */
export const cardMutedStyles: SxProps<Theme> = {
    ...(cardStyles as object),
    backgroundColor: 'rgba(16,24,40,0.015)',
};

export const cardHeaderStyles: SxProps<Theme> = {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { xs: 'flex-start', sm: 'center' },
    justifyContent: 'space-between',
    gap: 1.5,
};

export const serviceNameStyles: SxProps<Theme> = {
    fontWeight: 700,
};

export const chipRowStyles: SxProps<Theme> = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 1,
};

/** The dates and duration under the name: present, but never competing with it. */
export const metaRowStyles: SxProps<Theme> = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 2,
    mt: 0.5,
    color: 'text.secondary',
    fontSize: '0.85rem',
};

export const progressRowStyles: SxProps<Theme> = {
    mt: 2,
};

export const progressLabelStyles: SxProps<Theme> = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: 2,
    mb: 0.75,
};

export const remainingStyles: SxProps<Theme> = {
    fontWeight: 700,
};

export const progressBarStyles: SxProps<Theme> = {
    height: 8,
    borderRadius: 4,
};

/** Price on the right of the card, where a receipt would put it. */
export const priceBlockStyles: SxProps<Theme> = {
    textAlign: { xs: 'left', sm: 'right' },
};

export const priceStyles: SxProps<Theme> = {
    fontWeight: 700,
    whiteSpace: 'nowrap',
};

/** The struck-through list price, shown only when something was taken off it. */
export const strikePriceStyles: SxProps<Theme> = {
    textDecoration: 'line-through',
    color: 'text.disabled',
    fontSize: '0.85rem',
    whiteSpace: 'nowrap',
};

export const actionRowStyles: SxProps<Theme> = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 1,
    mt: 2,
};

/** Cancelling is destructive and rare, so it sits apart from the primary action. */
export const cancelButtonStyles: SxProps<Theme> = {
    ml: { sm: 'auto' },
};

/**
 * Sits where the record button would be. Takes the whole row, like the invoice hint it
 * mirrors, so a long Romanian sentence does not squeeze the actions beside it.
 */
export const allBookedHintStyles: SxProps<Theme> = {
    color: 'text.secondary',
    flexBasis: '100%',
};

export const notesStyles: SxProps<Theme> = {
    mt: 1.5,
    color: 'text.secondary',
    whiteSpace: 'pre-wrap',
};

export const sessionListStyles: SxProps<Theme> = {
    mt: 1,
    pt: 1,
    borderTop: '1px solid rgba(16,24,40,0.06)',
};

export const sessionRowStyles: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 2,
    py: 0.75,
};

export const sessionDateStyles: SxProps<Theme> = {
    fontWeight: 600,
    whiteSpace: 'nowrap',
};

export const sessionTherapistStyles: SxProps<Theme> = {
    color: 'text.secondary',
    fontSize: '0.85rem',
};

export const statusChipStyles: SxProps<Theme> = {
    ml: 1,
    height: 20,
    fontSize: '0.7rem',
};

/** The booked-but-not-yet-held count, sitting under the remainder it qualifies. */
export const scheduledLineStyles: SxProps<Theme> = {
    color: 'text.secondary',
    fontSize: '0.85rem',
};
